import { SHAutocomplete, SHStack } from "@components/design-systems";
import {
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { useTheme } from "@mui/material";
import React, { forwardRef, useMemo, useState } from "react";
import {
  EditMSFilterTrackRecordFieldPath,
  getMSFilterRenderedItemInitialProps,
  getMSFilterSelectedLovValue,
  MSFilterRenderedItemProps,
  MSFilterTrackRecordInitialProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { MSFilterResetIcon } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/reset";
import { useMount, useToggle } from "react-use";
import { Controller, useFormContext } from "react-hook-form";
import { isEqual, isNil } from "lodash";

export const MSFilterTrackRecord = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ item, itemSize, itemPaths, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette, shadows } = useTheme();
  const trackRecordFieldPath = itemPaths as EditMSFilterTrackRecordFieldPath;
  const { label, defaultDropdownOptions } =
    getMSFilterRenderedItemInitialProps<MSFilterTrackRecordInitialProps>(
      item,
    ) || {};

  const initialDropdownOptions = useMemo(
    () => defaultDropdownOptions || [],
    [defaultDropdownOptions],
  );

  const { control, getValues, setValue } =
    useFormContext<ModelScreenerFilterDTO>();

  const [isMounted, toggleMounted] = useToggle(false);
  const [currentValue, setCurrentValue] = useState<
    ModelScreenerFilterDropdownLov[]
  >([]);

  const handleOnDropdownValueChange = (
    value?: ModelScreenerFilterDropdownLov | undefined,
  ): ModelScreenerFilterDropdownLov[] => {
    if (isNil(value)) {
      setCurrentValue(initialDropdownOptions);
      return initialDropdownOptions;
    }

    const newValue = getMSFilterSelectedLovValue(currentValue, value);
    setCurrentValue(newValue);
    return newValue;
  };

  const handleOnReset = () => {
    setCurrentValue(initialDropdownOptions);
    setValue(trackRecordFieldPath.lov, initialDropdownOptions);
  };

  useMount(() => {
    const formValue = getValues(
      trackRecordFieldPath.lov,
    ) as ModelScreenerFilterDropdownLov[];

    if (!formValue) return;

    setCurrentValue(formValue);
    toggleMounted(true);
  });

  const selectedOption = useMemo(() => {
    return currentValue.find((opt) => opt.isSelected);
  }, [currentValue]);

  const shouldDisplayResetButton = useMemo(() => {
    return !isEqual(currentValue, initialDropdownOptions);
  }, [currentValue, initialDropdownOptions]);

  return (
    <SHStack
      ref={ref}
      padding={itemSize === "small" ? 1 : 1.5}
      bgcolor={palette.common.white}
      borderRadius={"12px"}
      boxShadow={shadows[1]}
      gap={0.5}
    >
      <SHStack
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <MSFilterItemLabel size={itemSize} disabled={disabled}>
          {label}
        </MSFilterItemLabel>
        {shouldDisplayResetButton && (
          <MSFilterResetIcon onClick={handleOnReset} disabled={disabled} />
        )}
      </SHStack>

      <SHStack key={`${isMounted}`}>
        <Controller
          control={control}
          name={trackRecordFieldPath.lov}
          render={({ field: { ref, ...other } }) => (
            <SHAutocomplete
              disabled={disabled}
              onChange={(_, lovItem) => {
                const newValue = handleOnDropdownValueChange(lovItem);
                other.onChange(newValue);
              }}
              value={selectedOption}
              options={initialDropdownOptions}
              disableClearable
              getOptionLabel={(option) => option.name ?? ""}
            />
          )}
        />
      </SHStack>
    </SHStack>
  );
});
